import { useEffect, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { AlertTriangle, MapPin } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDateTime } from "@/lib/format";

const FitBounds = ({ places }) => {
  const map = useMap();
  useEffect(() => {
    if (places.length === 1) map.setView([places[0].lat, places[0].lon], 6);
    if (places.length > 1) map.fitBounds(places.map((p) => [p.lat, p.lon]), { padding: [30, 30], maxZoom: 8 });
  }, [places, map]);
  return null;
};

const PlacePopup = ({ p }) => (
  <div className="text-xs">
    <p className="font-semibold">{p.location}</p>
    <p>{p.logins} sign-in{p.logins === 1 ? "" : "s"}{p.failed ? ` · ${p.failed} failed` : ""}{p.gps ? " · GPS" : ""}</p>
    <p className="text-slate-500">Last: {fmtDateTime(p.last_at)}</p>
    <p className="mt-1">{p.users.map((u) => u.name).join(", ")}</p>
    {p.new_for.length > 0 && <p className="mt-1 font-semibold text-rose-600">New place for: {p.new_for.join(", ")}</p>}
  </div>
);

const PlaceList = ({ places, onPick }) => (
  <ul className="max-h-[52vh] space-y-1.5 overflow-y-auto pr-1">
    {places.map((p) => (
      <li key={p.key}>
        <button onClick={() => onPick(p)} className={`w-full rounded-lg border p-2 text-left text-xs transition-colors hover:bg-blue-50 ${p.unusual ? "border-rose-200 bg-rose-50/60" : "border-slate-200 bg-white"}`} data-testid={`map-place-${p.key}`}>
          <p className="flex items-center gap-1 font-semibold text-slate-800">
            {p.unusual ? <AlertTriangle className="h-3 w-3 text-rose-600" /> : <MapPin className="h-3 w-3 text-blue-600" />}{p.location}
          </p>
          <p className="text-[11px] text-slate-500">{p.logins} sign-in{p.logins === 1 ? "" : "s"}{p.failed ? ` · ${p.failed} failed` : ""} · {p.users.length} user{p.users.length === 1 ? "" : "s"}</p>
          {p.new_for.length > 0 && <p className="text-[10px] font-medium text-rose-600">New place for {p.new_for.join(", ")}</p>}
        </button>
      </li>
    ))}
  </ul>
);

export const LoginMap = ({ token }) => {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [map, setMap] = useState(null);
  useEffect(() => {
    setData(null);
    api.get("/activity-logs/map", { params: { days }, headers: { "X-Logs-Token": token } })
      .then((r) => setData(r.data)).catch(() => setData({ places: [], total_signins: 0, total_failed: 0 }));
  }, [days, token]);
  const pick = (p) => map && map.setView([p.lat, p.lon], 9);

  return (
    <div className="min-w-0 space-y-3" data-testid="login-map">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="h-8 rounded-md border border-slate-200 bg-white px-2" data-testid="map-days">
          {[7, 30, 90, 365].map((d) => <option key={d} value={d}>Last {d} days</option>)}
        </select>
        {data && <span className="text-slate-600" data-testid="map-summary">{data.total_signins} sign-ins · {data.total_failed} failed · {data.places.length} places</span>}
        <span className="ml-auto flex items-center gap-3 text-[11px] text-slate-500">
          <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-blue-600" />Usual</span>
          <span className="flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-rose-600" />Unusual (new place or failed)</span>
        </span>
      </div>
      {!data ? <p className="py-10 text-center text-sm text-slate-500">Loading map...</p> : (
        <div className="grid gap-3 md:grid-cols-[1fr_240px]">
          <div className="h-[52vh] min-h-[280px] overflow-hidden rounded-lg border border-slate-200">
            <MapContainer center={[20.6, 78.9]} zoom={4} ref={setMap} className="h-full w-full" scrollWheelZoom>
              <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <FitBounds places={data.places} />
              {data.places.map((p) => (
                <CircleMarker key={p.key} center={[p.lat, p.lon]} radius={Math.min(8 + (p.logins + p.failed) * 2, 22)}
                  pathOptions={{ color: p.unusual ? "#E11D48" : "#2563EB", fillColor: p.unusual ? "#F43F5E" : "#3B82F6", fillOpacity: 0.55, weight: 2 }}>
                  <Popup><PlacePopup p={p} /></Popup>
                </CircleMarker>
              ))}
            </MapContainer>
          </div>
          {data.places.length ? <PlaceList places={data.places} onPick={pick} /> : <p className="py-6 text-center text-xs text-slate-500">No sign-ins with a known location in this period</p>}
        </div>
      )}
    </div>
  );
};
